// Persona detail — reader view + entry point to test scenarios (Slice 3).
//
// Shows attribute cards + a Test Scenarios section (empty in Slice 1;
// scenarios wire up in Slice 3). Provides Edit / Archive / Restore
// affordances per the RBAC-gated flow.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button } from '@gc-coach/design-system';
import type {
  Persona,
  PersonaConversationSummary,
} from '@gc-coach/shared';
import { ALL_DOMAINS } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { useSession } from '../auth/index.js';
import {
  archivePersona,
  getPersona,
  restorePersona,
} from '../api/endpoints.js';

export function PersonaDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  const [persona, setPersona] = useState<Persona | null>(null);
  const [scenarios, setScenarios] = useState<PersonaConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function refetch() {
    if (!id) return;
    const result = await getPersona(id);
    if (result.ok) {
      setPersona(result.data.persona);
      setScenarios([...result.data.scenarios]);
    } else {
      setError(t('personae.detailLoadError'));
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      await refetch();
      if (!cancelled) setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return null;

  async function onArchive() {
    setPending(true);
    setError(null);
    const result = await archivePersona(id!);
    setPending(false);
    if (result.ok) setPersona(result.data.persona);
    else setError(t('personae.archiveError'));
  }
  async function onRestore() {
    setPending(true);
    setError(null);
    const result = await restorePersona(id!);
    setPending(false);
    if (result.ok) setPersona(result.data.persona);
    else setError(t('personae.restoreError'));
  }

  const attrs = persona?.attributes;

  return (
    <AdminShell
      activeKey="personae"
      breadcrumb={[
        t('personae.breadcrumbKnowledge'),
        t('personae.breadcrumbPersonae'),
        persona?.name ?? '…',
      ]}
      state={isLoading && !persona ? { kind: 'loading' } : { kind: 'normal' }}
    >
      {persona && (
        <PageHeader
          title={persona.name}
          sub={
            persona.status === 'archived'
              ? t('personae.detailArchivedSub')
              : t('personae.detailActiveSub')
          }
          actions={
            <>
              <Button
                kind="ghost"
                onClick={() => navigate(`/admin/knowledge/personae/${id}/edit`)}
              >
                {t('personae.editButton')}
              </Button>
              {persona.status === 'active' ? (
                <Button kind="ghost" onClick={onArchive} disabled={pending}>
                  {t('personae.archiveButton')}
                </Button>
              ) : isSuperAdmin ? (
                <Button kind="primary" onClick={onRestore} disabled={pending}>
                  {t('personae.restoreButton')}
                </Button>
              ) : (
                <Button kind="ghost" disabled title={t('personae.restoreSuperAdminOnly')}>
                  {t('personae.restoreButton')}
                </Button>
              )}
              <Button
                kind="primary"
                onClick={() =>
                  navigate(`/admin/insights/labs?persona=${id}`)
                }
                disabled={persona.status === 'archived'}
              >
                {t('personae.openInLabsButton')}
              </Button>
            </>
          }
        />
      )}

      {error && (
        <div style={{ marginBottom: 12 }}>
          <Alert severity="danger">{error}</Alert>
        </div>
      )}

      {attrs && (
        <>
          <Card title={t('personae.groupIdentity')}>
            <KV label={t('personae.fieldSector')} value={attrs.sector} />
            <KV label={t('personae.fieldRevenueBand')} value={attrs.revenue_band} />
            <KV label={t('personae.fieldEmployeeBand')} value={attrs.employee_band} />
            <KV
              label={t('personae.fieldLegalDeptSize')}
              value={attrs.legal_dept_size?.toString()}
            />
          </Card>

          <Card title={t('personae.groupGeography')}>
            <KV
              label={t('personae.fieldGeographies')}
              value={
                attrs.geographies && attrs.geographies.length > 0
                  ? attrs.geographies.join(', ')
                  : undefined
              }
            />
            <KV
              label={t('personae.fieldPrimaryJurisdiction')}
              value={attrs.primary_jurisdiction}
            />
          </Card>

          <Card title={t('personae.groupGCProfile')}>
            <KV
              label={t('personae.fieldYearsExperience')}
              value={attrs.years_experience}
            />
            <KV
              label={t('personae.fieldBackground')}
              value={
                attrs.background
                  ? t(`personae.background${attrs.background}` as const)
                  : undefined
              }
            />
          </Card>

          <Card title={t('personae.groupMaturity')}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {ALL_DOMAINS.map((d) => (
                <div
                  key={d}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                  }}
                >
                  <span>{t(`personae.domain_${d}` as const)}</span>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      textTransform: 'uppercase',
                      fontSize: 10,
                      letterSpacing: '0.06em',
                      color: 'var(--brand)',
                    }}
                  >
                    {t(`personae.maturity_${attrs.maturity_per_domain[d]}` as const)}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title={t('personae.groupBusinessContext')}>
            <div
              style={{
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                color: 'var(--ink1)',
                lineHeight: 1.5,
              }}
            >
              {attrs.business_context_narrative ?? (
                <em style={{ color: 'var(--ink3)' }}>{t('personae.noContext')}</em>
              )}
            </div>
          </Card>

          <Card title={t('personae.testScenariosTitle')}>
            {scenarios.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ink3)' }}>
                {t('personae.noScenarios')}
              </div>
            ) : (
              scenarios.map((s) => (
                <div
                  key={s.id}
                  style={{
                    padding: '10px 0',
                    borderTop: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    navigate(
                      `/admin/insights/labs?persona=${id}&scenario=${s.id}`,
                    )
                  }
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      {s.message_count} messages · 👍 {s.up_count} · 👎 {s.down_count}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    {s.last_message_at
                      ? new Date(s.last_message_at).toLocaleDateString()
                      : '—'}
                  </div>
                </div>
              ))
            )}
          </Card>
        </>
      )}
    </AdminShell>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 16,
        marginBottom: 12,
        background: 'var(--paper)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--ink3)',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({
  label,
  value,
}: {
  label: string;
  value?: string | null | undefined;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr',
        gap: 12,
        padding: '6px 0',
        fontSize: 13,
      }}
    >
      <span style={{ color: 'var(--ink3)' }}>{label}</span>
      <span>{value ?? '—'}</span>
    </div>
  );
}
